import { Link } from "react-router-dom";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Welcome } from "@/components/Welcome";
import { Services } from "@/components/Services";
import { FamilyOffice } from "@/components/FamilyOffice";
import { Manifesto } from "@/components/Manifesto";
import { Testimonial } from "@/components/Testimonial";
import { Contact } from "@/components/Contact";
import { SEO } from "@/components/SEO";
import { cities } from "@/data/cities";
import { localize } from "@/utils/paths";
import { motion } from "framer-motion";

const LocationsStrip = ({ t, lang }) => (
  <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-36" data-testid="locations-strip">
    <motion.p
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="text-xs uppercase tracking-[0.25em] font-bold text-taupe mb-6"
    >
      {t.locationsStrip.overline}
    </motion.p>
    <motion.h2
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="font-heading font-light tracking-tight text-4xl md:text-5xl max-w-3xl leading-[1.05] mb-12"
      data-testid="locations-strip-title"
    >
      {t.locationsStrip.title}
    </motion.h2>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-line border border-line">
      {cities.map((c, i) => (
        <motion.div
          key={c.slug}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link
            to={localize(`/standorte/${c.slug}`, lang)}
            data-testid={`locations-strip-${c.slug}`}
            className="group block bg-cream hover:bg-sand transition-colors duration-300 px-6 py-8"
          >
            <span className="font-heading text-2xl md:text-3xl font-medium group-hover:text-taupe transition-colors duration-300">
              {c.name}
            </span>
            <span className="mt-2 block text-xs text-smoke tracking-wide">{c.canton[lang]}</span>
          </Link>
        </motion.div>
      ))}
    </div>
  </section>
);

const homeLd = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Swiss PA Services",
    url: "https://swisspaservice.ch",
    email: "info@swiss-pa-services.ch",
    telephone: "+41 44 688 03 80",
    address: { "@type": "PostalAddress", streetAddress: "Baarerstrasse 43", postalCode: "6300", addressLocality: "Zug", addressCountry: "CH" },
  },
  {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Swiss PA Services",
    url: "https://swisspaservice.ch",
    email: "info@swiss-pa-services.ch",
    telephone: "+41 44 688 03 80",
    address: { "@type": "PostalAddress", streetAddress: "Baarerstrasse 43", postalCode: "6300", addressLocality: "Zug", addressCountry: "CH" },
    geo: { "@type": "GeoCoordinates", latitude: 47.1736, longitude: 8.5135 },
    areaServed: ["Zug", "Zürich", "Genf", "Luzern", "Lugano", "Gstaad", "St. Moritz", "Verbier", "Zermatt", "Andermatt"],
  },
];

const Home = ({ t, scrollTo, lang }) => (
  <>
    <SEO
      title={
        lang === "de"
          ? "Persönliche Assistenz & Relocation Schweiz | Swiss PA Services"
          : "Personal Assistance & Relocation Switzerland | Swiss PA Services"
      }
      description={
        lang === "de"
          ? "Persönliche Assistenz, Relocation, Lifestyle Concierge & Zweitwohnsitz-Betreuung – diskret und persönlich, von Zug aus in der ganzen Schweiz."
          : "Personal assistance, relocation, lifestyle concierge & second home care – discreet and personal, from Zug across Switzerland."
      }
      path={localize("/", lang)}
      jsonLd={homeLd}
    />
    <Hero t={t} scrollTo={scrollTo} />
    <Marquee t={t} />
    <Welcome t={t} />
    <Services t={t} lang={lang} />
    <FamilyOffice t={t} lang={lang} />
    <LocationsStrip t={t} lang={lang} />
    <Manifesto t={t} />
    <Testimonial t={t} />
    <Contact t={t} lang={lang} />
  </>
);

export default Home;
